import React, { useState, useRef } from "react";
import styled from "styled-components";
import * as Sentry from "@sentry/react";
import ErrorText from "../library/ErrorText";
import Recruiter from "../../apiResources/Recruiter";
import Employer from "../../apiResources/Employer";
import RecruiterUser from "../../apiResources/RecruiterUser";
import EmployerUser from "../../apiResources/EmployerUser";
import SettingLabel from "./SettingLabel";
import { useDidMountEffect } from "../../hooks";
import { fileToBase64 } from "../../helpers";
import Button from "../library/Button";

type FileData = {
  data: string;
  filename: string;
};

const LogoSettingOption = (props: Props) => {
  const { user, company, disabled } = props;

  const fileInputRef = useRef<HTMLInputElement>();

  const [logo, setLogo] = useState<FileData>(null);
  const [error, setError] = useState<string>(null);
  const [isLoading, setIsLoading] = useState(false);

  const clearError = () => setError(null);

  const field = user.isEmployer ? "logo" : "profileImage";

  const updateCompany = async () => {
    setIsLoading(true);
    clearError();

    try {
      company.assignAttributes({ [field]: logo });
      const success = await company.save();
      if (!success) {
        setError(company.errors[field]?.fullMessage);
      }
    } catch (err) {
      Sentry.captureException(err);
      setError("Oops, something went wrong updating the logo");
    } finally {
      setIsLoading(false);
    }
  };

  useDidMountEffect(() => {
    if (!error) {
      void updateCompany();
    }
  }, [logo]);

  const onChange = async (file: File) => {
    const data = (await fileToBase64(file)) as string;
    setLogo({ data, filename: file.name });
  };

  return (
    <section>
      <Header>
        <SettingLabel>Logo</SettingLabel>
      </Header>
      <Row>
        <Logo src={company[`${field}Url`]} />
        {user.isAdmin && (
          <>
            <HiddenUploadInput
              ref={fileInputRef}
              accept="image/png,image/jpg,image/jpeg,image/webp"
              type="file"
              onChange={(event) => onChange(event.target.files[0])}
            />
            <Button
              secondary
              type="button"
              disabled={disabled || isLoading}
              onClick={() => fileInputRef.current.click()}
            >
              {isLoading ? "Uploading..." : "Upload"}
            </Button>
          </>
        )}
      </Row>
      {error && <ErrorText>{error}</ErrorText>}
    </section>
  );
};

type Props = {
  user: RecruiterUser | EmployerUser;
  company: Recruiter | Employer;
  disabled?: boolean;
};

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
`;

const Header = styled(Row)`
  margin-bottom: 1rem;
`;

const Logo = styled.img`
  max-width: 300px;
  max-height: 150px;
  margin-right: 1.5rem;
  margin-bottom: 1rem;
`;

const HiddenUploadInput = styled.input`
  &&& {
    display: none;
  }
`;

export default LogoSettingOption;
